import React, { useState } from 'react';
import { Container, Row, Col, Modal, Form, Button, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from '../utils/api';
import { useStoreSettings } from '../contexts/StoreSettingsContext';
import 'quill/dist/quill.snow.css';
import './Footer.css';
import { Instagram, Facebook, X, Youtube} from 'react-bootstrap-icons';

const Footer = () => {
    const { settings } = useStoreSettings();
    const socialLinks = [
        { name: 'Instagram', url: 'https://instagram.com/pugtrailventures', icon: <Instagram /> },
        { name: 'Facebook', url: 'https://www.facebook.com/profile.php?id=61592394186804', icon: <Facebook /> },
        { name: 'X', url: 'https://x.com/pugtrail', icon: <X /> },
        { name: 'YouTube', url: 'https://www.youtube.com/@pugtrail', icon: <Youtube /> },
    ];
    const [showWriteToUs, setShowWriteToUs] = useState(false);
    const [writeForm, setWriteForm] = useState({
        email: '', phone: '', subject: '', message: '', category: 'Support'
    });
    const [writeSubmitting, setWriteSubmitting] = useState(false);
    const [writeError, setWriteError] = useState('');
    const [writeSuccess, setWriteSuccess] = useState('');

    const handleWriteChange = (e) => {
        const { name, value } = e.target;
        setWriteForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleWriteSubmit = async (e) => {
        e.preventDefault();
        setWriteError('');
        setWriteSuccess('');
        try {
            setWriteSubmitting(true);
            await axios.post('/api/contact-messages', writeForm);
            setWriteSuccess('Your message has been sent! We will get back to you soon.');
            setWriteForm({ email: '', phone: '', subject: '', message: '', category: 'Support' });
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (Array.isArray(detail)) {
                setWriteError(detail.map((d) => d.msg).join(', '));
            } else {
                setWriteError(detail || 'Failed to send message. Please try again.');
            }
        } finally {
            setWriteSubmitting(false);
        }
    };
    const displayPhone = settings.contact_phone_country && settings.contact_phone
        ? `(${settings.contact_phone_country})${settings.contact_phone}`
        : null;

    return (
        <footer className="bg-dark text-light mt-auto py-4">
            <Container>
                <Row>
                    <Col lg={6} md={6} sm={12} className="mb-3">
                        <h5>{settings.store_name}</h5>
                        <p>Your trusted partner for quality products.</p>
                        <h6 className="footer-social-title mt-3">Follow us on:</h6>
                        <div className="footer-social-links">
                            {socialLinks.map((social) => (
                                <a
                                    key={social.name}
                                    href={social.url}
                                    className="text-light footer-social-link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`Visit our ${social.name} page`}
                                >
                                    {social.icon}
                                </a>
                            ))}
                        </div>
                    </Col>
                    <Col lg={3} md={3} sm={12} className="mb-3">
                        <h6>Quick Links</h6>
                        <ul className="list-unstyled">
                            {/* <li><a href="/" className="text-light">Home</a></li>
                            <li><a href="/" className="text-light">Products</a></li>
                            <li><a href="/login" className="text-light">Login</a></li> */}

                            <li>
                                <Link to="/return-policy" className="text-light footer-link-btn">
                                    Return Policy
                                </Link>
                            </li>
                            <li>
                                <Link to="/shipping-policy" className="text-light footer-link-btn">
                                    Shipping Policy
                                </Link>
                            </li>
                            <li>
                                <Link to="/privacy-policy" className="text-light footer-link-btn">
                                    Privacy and Data Protection Policy
                                </Link>
                            </li>
                        </ul>
                    </Col>
                    <Col lg={3} md={3} sm={12} className="mb-3">
                        <h6>Contact</h6>
                        <p className="mb-0">Email: {settings.contact_email || 'Not configured'}</p>
                        <p className="mb-0">Phone: {displayPhone || 'Not configured'}</p>

                        <button
                            className="text-light footer-link-btn"
                            onClick={() => setShowWriteToUs(true)}
                        >
                            Write to Us
                        </button>

                    </Col>
                </Row>
                <hr className="my-3" />
                <Row>
                    <Col className="text-center">
                        <p className="mb-0">&copy; 2026 {settings.store_name}. All rights reserved.</p>
                        <p className="mb-0">Powered by Pugtrail retail platform.</p>
                    </Col>
                </Row>
            </Container>

            <Modal show={showWriteToUs} onHide={() => { setShowWriteToUs(false); setWriteError(''); setWriteSuccess(''); }} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Write to Us</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {writeSuccess ? (
                        <Alert variant="success">{writeSuccess}</Alert>
                    ) : (
                        <Form onSubmit={handleWriteSubmit}>
                            {writeError && <Alert variant="danger">{writeError}</Alert>}
                            <Row className="g-3">
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Email Address <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={writeForm.email}
                                            onChange={handleWriteChange}
                                            placeholder="your@email.com"
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Phone Number</Form.Label>
                                        <Form.Control
                                            type="tel"
                                            name="phone"
                                            value={writeForm.phone}
                                            onChange={handleWriteChange}
                                            placeholder="Optional"
                                            maxLength={20}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Category <span className="text-danger">*</span></Form.Label>
                                        <Form.Select name="category" value={writeForm.category} onChange={handleWriteChange} required>
                                            <option value="Partnership">Partnership</option>
                                            <option value="Support">Support</option>
                                            <option value="Feedback">Feedback</option>
                                            <option value="Suggestion">Suggestion</option>
                                            <option value="Others">Others</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>Subject <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="subject"
                                            value={writeForm.subject}
                                            onChange={handleWriteChange}
                                            placeholder="Brief subject"
                                            maxLength={200}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12}>
                                    <Form.Group>
                                        <Form.Label>Message <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={5}
                                            name="message"
                                            value={writeForm.message}
                                            onChange={handleWriteChange}
                                            placeholder="Write your message here…"
                                            required
                                            minLength={10}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <div className="d-grid mt-3">
                                <Button type="submit" variant="primary" disabled={writeSubmitting}>
                                    {writeSubmitting ? 'Sending…' : 'Send Message'}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Modal.Body>
            </Modal>
        </footer>
    );
};

export default Footer;
